import { ThemeUICSSObject } from 'theme-ui';

export const inputFocusedStyles: ThemeUICSSObject = {
  borderColor: 'primary',
  boxShadow: 'focus.primary',
};
export const inputDisabledStyles: ThemeUICSSObject = {
  borderColor: ({ colors }) => `${colors?.accent} !important`,
  opacity: 0.5,
  cursor: 'initial',
};

export const defaultCheckboxStyles: ThemeUICSSObject = {
  color: 'greyScale.4',
  backgroundColor: 'background',
  border: () => `1px solid`,
  mr: 3,
  display: 'flex',
  borderRadius: 4,
  flexShrink: 0,
  minWidth: 24,
  minHeight: 24,
  outline: 'none !important',
  transition: 'all 0.2s ease',
  svg: {
    transition: 'all 0.2s ease',
    color: 'primary',
  },
  'input:hover:not(:disabled) ~ &': {
    color: 'primary',
  },
  'input:checked ~ &': {
    color: 'primary',
    svg: {
      color: 'background',
    },
    backgroundColor: 'primary',
  },
};

export const defaultRadioStyles: ThemeUICSSObject = {
  color: 'greyScale.4',
  border: () => `1px solid`,
  mr: 3,
  backgroundColor: 'background',
  borderRadius: 'full',
  position: 'relative',
  flexShrink: 0,
  height: '24px',
  width: '24px',
  outline: 'none !important',
  transition: 'all 0.3s ease',
  ':before': {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transition: 'all 0.3s ease',
    transform: 'translateX(-50%) translateY(-50%) scale(2)',
    display: 'block',
    content: '""',
    backgroundColor: 'background',
    height: '10px',
    width: '10px',
    borderRadius: 'full',
  },
  'input:active + &': {
    backgroundColor: 'blueScale.2',
  },
  'input:checked + &': {
    borderColor: 'primary',
    backgroundColor: 'primary',
    ':before': {
      transform: 'translateX(-50%) translateY(-50%) scale(1)',
    },
  },
  'input:checked + &:before': {
    backgroundColor: 'background',
  },
  'input:checked:not(:focus) + &': {
    borderColor: 'transparent',
  },
  'input:disabled + &': {
    borderColor: 'senaryScale.3',
  },
};

export const inputInvalidStyles: ThemeUICSSObject = {
  borderColor: 'redScale.4',
  ':focus-within': {
    borderColor: 'redScale.4',
    boxShadow: 'focus.error',
  },
};

export const forms = {
  label: {
    display: 'flex',
    '&[aria-disabled=true]': {
      opacity: 0.7,
    },
    cursor: 'pointer',
  } as ThemeUICSSObject,
  input: {
    transition: 'all 0.1s linear',
    position: 'relative',
    outline: 'none',
    color: 'text',
    fontFamily: 'body',
    border: '1px solid transparent',
    boxShadow: 'none',
    background: 'transparent',
    padding: 0,
    '::placeholder': {
      color: 'highlight',
    },
    ':focus': {
      '::placeholder': {
        color: 'accent',
      },
    },
    '&::-webkit-inner-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    '&::-webkit-outer-spin-button': {
      WebkitAppearance: 'none',
      margin: 0,
    },
    '&::-ms-clear': {
      display: 'none',
    },
  } as ThemeUICSSObject,
  inputContainer: {
    transition: 'all 0.1s linear',
    position: 'relative',
    outline: 'none',
    fontFamily: 'body',
    color: 'text',
    paddingX: 4,
    borderRadius: 'md',
    ':hover:has(input:is([aria-invalid=false]))': {
      borderColor: 'primary',
    },
    border: ({ colors }) => `1px solid ${colors?.accent}`,
    display: 'flex',
    minHeight: 'input',
    ':focus:not(:invalid, &[aria-invalid=true])': {
      ...inputFocusedStyles,
    },
    ':invalid, &[aria-invalid=true]': {
      ...inputInvalidStyles,
    },
    ':disabled': {
      ...inputDisabledStyles,
    },
  } as ThemeUICSSObject,
  select: {
    variant: 'forms.inputContainer',
    height: 'full',
    cursor: 'pointer',
  } as ThemeUICSSObject,
  checkbox: {
    plain: {
      ...defaultCheckboxStyles,
      'input:focus ~ &': {
        boxShadow: 'focus.primary',
      },
    } as ThemeUICSSObject,
    containerized: {
      ...defaultCheckboxStyles,
    } as ThemeUICSSObject,
  },
  checkboxContainer: {
    paddingY: 2,
    paddingX: 3,
    border: `1px solid transparent`,
    width: 'full',
    boxShadow: 'xs',
    transition: 'all 0.1s linear',
    position: 'relative',
    outline: 'none',
    fontFamily: 'body',
    color: 'text',
    borderRadius: 'md',
    ':hover': {
      borderColor: 'primary',
    },
    display: 'flex',
    minHeight: 'input',
    ':focus:not(:invalid)': {
      ...inputFocusedStyles,
    },
    ':invalid': {
      ...inputInvalidStyles,
    },
    ':disabled': {
      ...inputDisabledStyles,
    },
  } as ThemeUICSSObject,
  radio: {
    plain: {
      ...defaultRadioStyles,
      'input:focus ~ &': {
        boxShadow: 'focus.primary',
      },
    } as ThemeUICSSObject,
    containerized: {
      ...defaultRadioStyles,
    } as ThemeUICSSObject,
  },
  radioContainer: {
    paddingY: 2,
    paddingX: 3,
    border: `1px solid transparent`,
    width: 'full',
    boxShadow: 'xs',
    transition: 'all 0.1s linear',
    position: 'relative',
    outline: 'none',
    fontFamily: 'body',
    color: 'text',
    borderRadius: 'md',
    ':hover': {
      borderColor: 'primary',
    },
    display: 'flex',
    minHeight: 'input',
    ':focus:not(:invalid)': {
      ...inputFocusedStyles,
    },
    ':invalid': {
      ...inputInvalidStyles,
    },
    ':disabled': {
      ...inputDisabledStyles,
    },
  } as ThemeUICSSObject,
  textarea: {
    variant: 'forms.inputContainer',
    resize: 'none',
    padding: 4,
  } as ThemeUICSSObject,
};
